import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'statusFilter',
    'experimentsList',
    'suggestionsBtn',
    'suggestionsModal',
    'suggestionsContent'
  ]

  static values = {
    accountId: String,
    userId: String
  }

  connect() {
    // Initialize any needed state
  }

  filterByStatus(event) {
    const status = event.target.value
    const cards = this.experimentsListTarget.querySelectorAll('[data-experiment-id]')

    cards.forEach((card) => {
      const statusBadge = card.querySelector('[class*="text-xs"][class*="font-bold"]')
      if (!statusBadge) return

      const cardStatus = statusBadge.textContent.trim().toLowerCase()

      if (!status || cardStatus === status) {
        card.style.display = ''
      } else {
        card.style.display = 'none'
      }
    })
  }

  async getSuggestions() {
    this.suggestionsBtnTarget.disabled = true
    this.suggestionsBtnTarget.textContent = 'Loading...'

    try {
      const response = await fetch('/api/v1/experiments/suggestions', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      })

      if (!response.ok) throw new Error('Failed to load suggestions')

      const data = await response.json()
      this.renderSuggestions(data.suggestions || [])
      this.openSuggestionsModal()
    } catch (error) {
      console.error('Error loading suggestions:', error)
      this.showNotification('Failed to load suggestions. Please try again.', 'error')
    } finally {
      this.suggestionsBtnTarget.disabled = false
      this.suggestionsBtnTarget.textContent = 'Get Suggestions'
    }
  }

  renderSuggestions(suggestions) {
    if (suggestions.length === 0) {
      this.suggestionsContentTarget.innerHTML = `
        <div class="text-center py-8 text-gray-500">
          <p>No suggestions available yet. Keep tracking your health data for personalized recommendations!</p>
        </div>
      `
      return
    }

    const html = suggestions.map((suggestion) => `
      <div class="p-4 border-2 border-gray-200 hover:border-gray-400 transition-colors">
        <h4 class="font-bold text-lg mb-2">${this.escapeHtml(suggestion.name)}</h4>
        <p class="text-gray-600 mb-3">${this.escapeHtml(suggestion.hypothesis || '')}</p>
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-500">
            ${suggestion.variables ? `<span>${suggestion.variables.length} variable(s)</span>` : ''}
            ${suggestion.metrics ? `<span class="ml-3">${suggestion.metrics.length} metric(s)</span>` : ''}
          </div>
          <button type="button"
                  class="px-4 py-2 bg-gray-900 text-white text-sm font-bold hover:bg-gray-700"
                  data-action="click->dashboard--experiments#createFromSuggestion"
                  data-suggestion="${this.escapeHtml(JSON.stringify(suggestion))}">
            Create Experiment
          </button>
        </div>
      </div>
    `).join('')

    this.suggestionsContentTarget.innerHTML = html
  }

  async createFromSuggestion(event) {
    const suggestion = JSON.parse(event.target.dataset.suggestion)

    // Navigate to new experiment form with pre-filled data
    const params = new URLSearchParams({
      name: suggestion.name || '',
      hypothesis: suggestion.hypothesis || '',
      variables: JSON.stringify(suggestion.variables || []),
      metrics: JSON.stringify(suggestion.metrics || [])
    })

    window.location.href = `/dashboard/experiments/new?${params.toString()}`
  }

  openSuggestionsModal() {
    this.suggestionsModalTarget.classList.remove('hidden')
    document.body.style.overflow = 'hidden'
  }

  closeSuggestions() {
    this.suggestionsModalTarget.classList.add('hidden')
    document.body.style.overflow = ''
  }

  showNotification(message, type = 'success') {
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 p-4 border-2 ${
      type === 'success' ?
        'bg-green-50 border-green-500 text-green-700' :
        'bg-red-50 border-red-500 text-red-700'
    }`
    notification.textContent = message

    document.body.appendChild(notification)

    setTimeout(() => {
      notification.remove()
    }, 3000)
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }
}
